import { AdEnginePlugin, type Hooks } from "../../../../core/AdEnginePlugin";
import type {
  IasBrandSafetyValue,
  IasSlotConfig,
  IasTargetingData,
} from "./types";
import type { InstantConfigRepository } from "instant-config";
import { loadScript, pushTrack } from "utils";
import type { GptWrapper } from "../../../../core/GptWrapper";
import type { SlotName } from "../../../../config/platforms/types";
import type { AdNodeManager } from "../../../../core/AdNodeManager";
import { getApp } from "../../../../helpers/getApp";
import type { AdNode } from "../../../../core/AdNode";
import {
  IAS_SCRIPT_URL,
  IAS_PUB_ID,
  IAS_BRAND_SAFETY_KEYS,
  IAS_WATCHED_SLOT_LIST,
  IAS_BRAND_SAFETY_VALUES_LEVEL,
} from "./consts";
import { extractSingleSizeFromGeneralSize } from "../../../../helpers/extractSingleSizeFromGeneralSize";
import { type FandomCmp, LoadTimeEvent } from "common-types";
import type { AdEngine } from "../../../../core/AdEngine";
import { isConsentGranted } from "../../../../helpers/isConsentGranted";

type IasPublisherOptimizationPluginConfig = Pick<
  InstantConfigRepository,
  "icIASPublisherOptimization"
>;

export class IasPublisherOptimizationPlugin extends AdEnginePlugin {
  private gptWrapper?: GptWrapper;
  private adNodeManager?: AdNodeManager;
  private consent = false;
  private initialized = false;

  static isEnabled(config: IasPublisherOptimizationPluginConfig): boolean {
    return Boolean(config?.icIASPublisherOptimization);
  }

  private async initialize(): Promise<void> {
    if (
      !this.consent ||
      !this.gptWrapper ||
      !this.adNodeManager ||
      this.initialized
    ) {
      return;
    }

    try {
      pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, {
        eventName: "ias-publisher-optimization_started",
        time: performance.now(),
      });
      await loadScript(IAS_SCRIPT_URL);
      this.setup();
      this.initialized = true;
      pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, {
        eventName: "ias-publisher-optimization_loaded",
        time: performance.now(),
      });
    } catch (error) {
      pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, {
        eventName: "ias-publisher-optimization_error_loading",
        time: performance.now(),
      });
      console.error("Failed to load IAS Publisher Optimization script", error);
    }
  }

  private setup(): void {
    this.setInitialTargeting();

    window.__iasPET = window.__iasPET ?? {};
    window.__iasPET.queue = window.__iasPET.queue ?? [];
    window.__iasPET.pubId = IAS_PUB_ID;
    window.__iasPET.queue.push({
      adSlots: this.getPETSlots(),
      dataHandler: this.iasDataHandler.bind(this),
    });
  }

  private setInitialTargeting(): void {
    this.gptWrapper?.setTargeting("fr", "-1");
    this.gptWrapper?.setTargeting("b_ias", "-1");
    this.gptWrapper?.setTargeting("ias-kw", "-1");

    IAS_BRAND_SAFETY_KEYS.forEach((key) => {
      this.gptWrapper?.setTargeting(key, "-1");
    });

    this.getWatchedAdNodes().forEach((node) => {
      node.setTargeting("vw", "-1");
    });
  }

  private getPETSlots(): IasSlotConfig[] {
    const iasPETSlots: IasSlotConfig[] = [];

    this.getWatchedAdNodes().forEach((node) => {
      const size = this.getSlotSize(node);

      const config: IasSlotConfig = {
        adUnitPath: node.getAdUnitPath() ?? "",
        adSlotId: node.getSlotName(),
        size,
      };

      if (node.getSlotType() === "video") {
        config.type = "video";
        config.size = [1, 1];
      }

      iasPETSlots.push(config);
    });

    return iasPETSlots;
  }

  private getSlotSize(node: AdNode): number[] {
    const { sizes } = node.getSlotConfig();
    const singleSize = extractSingleSizeFromGeneralSize(sizes);
    if (Array.isArray(singleSize)) {
      return singleSize;
    }
    return [1, 1];
  }

  private getWatchedAdNodes(): AdNode[] {
    const currentAdNodes = this.adNodeManager?.getAdNodes() ?? [];
    return currentAdNodes.filter((adNode) => {
      // Check if the baseSlotName or slotName matches a slot name expected to be tracked by DV
      return (
        this.getWatchedSlotList().includes(adNode.getSlotName()) ||
        this.getWatchedSlotList().includes(adNode.getBaseSlotName())
      );
    });
  }

  private getWatchedSlotList(): string[] {
    return IAS_WATCHED_SLOT_LIST[getApp()] ?? IAS_WATCHED_SLOT_LIST.default;
  }

  private iasDataHandler(adSlotData: string): void {
    try {
      const iasTargetingData: IasTargetingData = JSON.parse(adSlotData);
      this.updateGenericTargeting(iasTargetingData);
      this.updateSlotTargeting(iasTargetingData);
      this.updateBrandSafetyTargeting(iasTargetingData);
    } catch (error) {
      console.error("Failed to parse IAS targeting data", error);
    }
  }

  private updateGenericTargeting(iasTargetingData: IasTargetingData): void {
    this.gptWrapper?.setTargeting("fr", iasTargetingData?.fr ?? "");
    if (iasTargetingData.custom && iasTargetingData.custom["ias-kw"]) {
      this.gptWrapper?.setTargeting(
        "ias-kw",
        iasTargetingData.custom["ias-kw"],
      );
    }
  }

  private updateSlotTargeting(iasTargetingData: IasTargetingData): void {
    if (iasTargetingData.slots) {
      for (const [slotName, slotTargeting] of Object.entries(
        iasTargetingData.slots,
      )) {
        this.gptWrapper?.setSlotTargeting(
          slotName as SlotName,
          "vw",
          slotTargeting.vw ?? slotTargeting.vw_vv,
        );
      }
    }
  }

  private updateBrandSafetyTargeting(iasTargetingData: IasTargetingData): void {
    if (iasTargetingData.brandSafety) {
      const brandSafetyData = iasTargetingData.brandSafety;
      // the default value is -1
      let maxValue: IasBrandSafetyValue | undefined;

      IAS_BRAND_SAFETY_KEYS.forEach((key) => {
        if (brandSafetyData[key]) {
          this.gptWrapper?.setTargeting(key, brandSafetyData[key]);

          if (
            typeof maxValue === "undefined" ||
            IAS_BRAND_SAFETY_VALUES_LEVEL[maxValue] <
              IAS_BRAND_SAFETY_VALUES_LEVEL[brandSafetyData[key]]
          ) {
            maxValue = brandSafetyData[key];
          }
        }
      });

      this.gptWrapper?.setTargeting("b_ias", maxValue ?? "-1");
    }
  }

  hooks: Hooks = {
    /**
     * Hook triggered when consent is updated.
     * @param fandomCmp - The consent parameters.
     */
    onConsentUpdated: (fandomCmp: FandomCmp) => {
      if (!isConsentGranted(fandomCmp)) {
        return;
      }

      this.consent = true;
      this.initialize();
    },
    onAdEngineInit: (adEngine: AdEngine) => {
      this.adNodeManager = adEngine.adNodeManager;
      this.gptWrapper = adEngine.gptWrapper;
      this.initialize();
    },
  };
}
